<script setup>
import { Link, router } from '@inertiajs/vue3'
import TextInput from '@/Shared/TextInput.vue'
import LoadingButton from '@/Shared/LoadingButton.vue'
import Portrait from '@/Shared/Portrait.vue'
import Icon from '@/Shared/Icon.vue'
import { useForm } from '@inertiajs/vue3'
import { useConfirm } from '@/Composables/useConfirm'
import { useDate } from '@/Composables/useDate'

const props = defineProps({
  player: {
    type: Object,
    required: true
  }
})

const breadcrumbs = [
  { label: 'Jugadores', url: '/players' },
  { label: `${props.player.name} ${props.player.lastname}` }
]

// Generar un color basado en el ID del jugador para el avatar
const getPlayerColor = (id) => {
  const colors = [
    '#4F46E5', // Indigo
    '#0EA5E9', // Sky
    '#10B981', // Emerald
    '#F59E0B', // Amber
    '#EF4444', // Red
    '#8B5CF6', // Violet
    '#EC4899', // Pink
    '#14B8A6' // Teal
  ]

  return colors[id % colors.length]
}

// Calcular iniciales para avatar
const getInitials = (firstName, lastName) => {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`
}

let form = useForm({
  name: props.player.name,
  lastname: props.player.lastname,
  phone: props.player.phone,
  email: props.player.email || '',
  birthdate: props.player.birthdate || '',

  notes: props.player.notes || '',
  telegram_chat_id: props.player.telegram_chat_id || ''
})

function update() {
  form.put(`/players/${props.player.id}/update`)
}

async function remove() {
  if (
    await useConfirm(
      '¿Estás seguro de que quieres eliminar este jugador? Esta acción no se puede deshacer.'
    )
  ) {
    router.delete(`/players/${props.player.id}/delete`)
  }
}
</script>

<template>
  <Portrait
    :title="`${form.name} ${form.lastname}`"
    subtitle="Editar Información"
    :breadcrumbs="breadcrumbs"
    :footer-actions="true"
  >
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <!-- Columna principal con el formulario -->
      <div class="lg:col-span-2 space-y-6">
        <!-- Tarjeta principal del formulario -->
        <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <!-- Cabecera del formulario con avatar -->
          <div class="p-5 border-b border-gray-100 flex items-center">
            <div
              class="rounded-full h-12 w-12 flex items-center justify-center font-bold text-white mr-3"
              :style="`background-color: ${getPlayerColor(props.player.id)};`"
            >
              {{ getInitials(form.name, form.lastname) }}
            </div>
            <div>
              <h2 class="font-semibold text-secondary-900">{{ form.name }} {{ form.lastname }}</h2>
              <div class="text-xs text-secondary-500">ID: {{ props.player.id }}</div>
            </div>
          </div>

          <!-- Formulario -->
          <form @submit.prevent="update">
            <div class="p-5 space-y-6">
              <!-- Datos personales -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <TextInput v-model="form.name" :error="form.errors.name" label="Nombre" required />
                <TextInput
                  v-model="form.lastname"
                  :error="form.errors.lastname"
                  label="Apellidos"
                  required
                />
              </div>

              <!-- Datos de contacto -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <TextInput
                  v-model="form.phone"
                  :error="form.errors.phone"
                  label="Teléfono"
                  type="tel"
                  required
                />
                <TextInput
                  v-model="form.email"
                  :error="form.errors.email"
                  label="Email"
                  type="email"
                />
              </div>

              <!-- Datos adicionales -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <TextInput
                  v-model="form.birthdate"
                  :error="form.errors.birthdate"
                  label="Fecha de nacimiento"
                  type="date"
                />
              </div>

              <!-- Telegram ID -->
              <div>
                <TextInput
                  :model-value="form.telegram_chat_id"
                  :error="form.errors.telegram_chat_id"
                  label="Telegram ID"
                  disabled
                />
                <p class="mt-1 text-xs text-secondary-500">
                  El ID de Telegram se asigna automáticamente cuando el jugador se conecta a través
                  del bot.
                </p>
              </div>

              <!-- Notas -->
              <div>
                <label class="form-label mb-2 block">Notas adicionales</label>
                <textarea
                  v-model="form.notes"
                  class="form-input w-full rounded-md border-gray-300 focus:border-primary-500 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                  rows="3"
                ></textarea>
                <div v-if="form.errors.notes" class="form-error mt-1">{{ form.errors.notes }}</div>
              </div>
            </div>
          </form>
        </div>
      </div>

      <!-- Columna lateral con información adicional -->
      <div class="space-y-6">
        <!-- Tarjeta de estadísticas -->
        <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div class="p-5 border-b border-gray-100 flex items-center">
            <div class="bg-primary-50 p-2 rounded-lg mr-3">
              <Icon name="ChartBar" class="h-5 w-5 text-primary-500" />
            </div>
            <h3 class="font-semibold text-secondary-900">Estadísticas</h3>
          </div>
          <div class="p-5 space-y-4">
            <div class="flex justify-between items-center pb-3 border-b border-gray-100">
              <span class="text-sm text-secondary-700">Clases asistidas</span>
              <span class="font-medium text-secondary-900">{{
                props.player.classes_attended || 0
              }}</span>
            </div>
            <div class="flex justify-between items-center pb-3 border-b border-gray-100">
              <span class="text-sm text-secondary-700">Última asistencia</span>
              <span class="font-medium text-secondary-900">{{
                props.player.last_attendance || 'N/A'
              }}</span>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-sm text-secondary-700">Fecha de registro</span>
              <span class="font-medium text-secondary-900">{{
                useDate(props.player.created_at).diffForHumans() || 'N/A'
              }}</span>
            </div>
          </div>
        </div>

        <!-- Tarjeta de acciones rápidas -->
        <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div class="p-5 border-b border-gray-100 flex items-center">
            <div class="bg-primary-50 p-2 rounded-lg mr-3">
              <Icon name="Bolt" class="h-5 w-5 text-primary-500" />
            </div>
            <h3 class="font-semibold text-secondary-900">Acciones rápidas</h3>
          </div>
          <div class="p-5 space-y-3">
            <Link
              href="#"
              class="flex items-center p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <div class="bg-blue-100 p-2 rounded-lg mr-3">
                <Icon name="Calendar" class="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <div class="font-medium text-secondary-900">Ver horario</div>
                <div class="text-xs text-secondary-500">Consulta las clases programadas</div>
              </div>
            </Link>
            <Link
              href="#"
              class="flex items-center p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <div class="bg-green-100 p-2 rounded-lg mr-3">
                <Icon name="Mail" class="h-4 w-4 text-green-600" />
              </div>
              <div>
                <div class="font-medium text-secondary-900">Enviar mensaje</div>
                <div class="text-xs text-secondary-500">Contacta directamente con el jugador</div>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex items-stretch justify-end w-full space-x-4">
        <button class="btn-red" tabindex="-1" type="button" @click="remove">Eliminar</button>

        <LoadingButton :loading="form.processing" class="btn-indigo" @click="update">
          Guardar
        </LoadingButton>
      </div>
    </template>
  </Portrait>
</template>
